import DOMPurify from 'dompurify';
import {
	sessionService,
	storageService,
	type ltFramework
} from '@life-time-inc/enterprise-framework-core';
import { getUtmParams, appendUtmData } from '@/util/utm';
import type { Location } from 'Types/aemui/find-a-location/findaLocation';

export const findImage = (club: Location | ltFramework.IClub, ratio: '1x1' | '16x9') => {
	return club.images?.find((image) => image?.ratio === ratio);
};

export const getStartingAtPriceLinkOverrideText = (club: Location | ltFramework.IClub): string => {
	const raw = 'startingAtPriceLinkOverrideText' in club ? club.startingAtPriceLinkOverrideText : '';

	if (!raw) return '';

	return DOMPurify.sanitize(String(raw), {
		ALLOWED_TAGS: [],
		ALLOWED_ATTR: []
	}).trim();
};

const getSurchargeStatus = (club: Location | ltFramework.IClub) => {
	if ('isSurchargeIncluded' in club) {
		return club.isSurchargeIncluded;
	}
	return false;
};

export const isWebPreSaleWaitlist = (club: Location | ltFramework.IClub) => {
	const { webPreSaleWaitlist } = club;
	return webPreSaleWaitlist === '1';
};

export const isOpenClub = (club: Location | ltFramework.IClub) => {
	const { open } = club;
	return open === 'open';
};

export const joinLinkText = (club: Location | ltFramework.IClub) => {
	const startingAtPriceLinkOverrideText = getStartingAtPriceLinkOverrideText(club);
	if (startingAtPriceLinkOverrideText) {
		return startingAtPriceLinkOverrideText;
	}
	if (isWebPreSaleWaitlist(club) && isOpenClub(club)) {
		return 'Reserve Membership';
	}
	if (isWebPreSaleWaitlist(club) && !isOpenClub(club)) {
		return 'Join the Waitlist';
	}
	return 'Join Today';
};

export const showSurchargeDisclaimer = (club: Location | ltFramework.IClub) => {
	const startingAtPriceLinkOverrideText =
		'startingAtPriceLinkOverrideText' in club ? club.startingAtPriceLinkOverrideText : null;
	if (startingAtPriceLinkOverrideText) {
		return false;
	}
	if (isWebPreSaleWaitlist(club) && isOpenClub(club)) {
		return false;
	}
	if (isWebPreSaleWaitlist(club) && !isOpenClub(club)) {
		return false;
	}
	return getSurchargeStatus(club);
};

export const hasJoinLink = (club: Location | ltFramework.IClub) => {
	return (
		club.open === 'open' && club.oms === 'Yes' && (club.clubTypeId === 1 || club.clubTypeId === 2)
	);
};

export const getReferralInfo = () => {
	const memberReferralCookieName = 'memberReferral';
	let referralCode = '';
	let referralSource = '';
	const storedReferralInfo = sessionService.getCookie(memberReferralCookieName);
	if (storedReferralInfo) {
		referralCode = storedReferralInfo.split('|')[0] ?? '';
		referralSource = storedReferralInfo.split('|')[1] ?? '';
	}
	return { referralCode, referralSource };
};

export const getPromotionInfo = () => {
	const cookieCorpMembership = 'corpMembership';
	const promotionURLParamConst = 'promotionName';
	let promotionName = '';
	const url = new URL(window.location.href);
	const promotionURLParam = url.searchParams.get(promotionURLParamConst);

	const storedPromotionInfo = sessionService.getCookie(cookieCorpMembership);

	if (promotionURLParam !== null) {
		promotionName = promotionURLParam;
		storageService.sessionSet(cookieCorpMembership, `${promotionName}|LTA-YTT`);
	} else if (storedPromotionInfo) {
		promotionName = storedPromotionInfo.split('|')[0] ?? '';
	}
	return promotionName;
};

const getWaitlistUrl = (club: Location | ltFramework.IClub) => {
	if ('waitlistURL' in club) {
		return club.waitlistURL;
	}
	if ('waitlistUrl' in club) {
		return club.waitlistUrl;
	}
	return null;
};

export const joinLink = (club: Location | ltFramework.IClub) => {
	const startingAtPriceOverrideLink =
		'startingAtPriceOverrideLink' in club ? club.startingAtPriceOverrideLink : null;
	const waitlistURL = getWaitlistUrl(club);
	const webPreSaleWaitlist = 'webPreSaleWaitlist' in club ? club.webPreSaleWaitlist : null;
	const isWaitlist = webPreSaleWaitlist === '1' && !!waitlistURL;

	let path = window.lt.globalPageDrivers.global?.transition ?? '';
	if (isWaitlist) {
		path = waitlistURL!;
	}
	if (startingAtPriceOverrideLink) {
		path = startingAtPriceOverrideLink;
	}
	if (!path) {
		return '';
	}
	let url: URL;
	try {
		url = new URL(path, window.location.origin);
	} catch {
		return '';
	}
	if (!startingAtPriceOverrideLink && !isWaitlist) {
		url.searchParams.append('clubId', String(club.mmsClubId));
	}

	const { referralCode, referralSource } = getReferralInfo();
	if (referralCode) {
		url.searchParams.append('source', referralSource);
		url.searchParams.append('referral', referralCode);
	}

	const promotionName = getPromotionInfo();
	if (promotionName) {
		url.searchParams.append('promotionName', promotionName);
		url.searchParams.append('source', 'web');
	}

	const utmData = getUtmParams();
	const isUtmEmpty = Object.values(utmData).every((utm) => utm === '');
	if (!isUtmEmpty) {
		appendUtmData(utmData, url.searchParams);
	}

	if (!isWaitlist && !startingAtPriceOverrideLink && !hasJoinLink(club)) {
		return '';
	}
	return url.toString();
};
